import { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { motion } from "framer-motion";
import { MapPin, Navigation, LocateFixed, ArrowUpRight } from "lucide-react";
import { fetchBranches } from "../../data/api";

const makeIcon = (active) =>
  L.divIcon({
    className: "",
    html: `<div style="position:relative;width:26px;height:34px">
      <div style="width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);position:absolute;left:2px;top:2px;background:${
        active ? "#E04F33" : "#F59E0B"
      };box-shadow:0 4px 10px rgba(0,0,0,.5);border:2px solid ${active ? "#ff6b5c" : "#fff2d8"}"></div>
      <div style="width:7px;height:7px;border-radius:50%;background:#141110;position:absolute;left:9.5px;top:9px;transform:rotate(0)"></div>
    </div>`,
    iconSize: [26, 34],
    iconAnchor: [13, 34],
    popupAnchor: [0, -30],
  });

const icon = makeIcon(false);
const iconActive = makeIcon(true);

const distKm = (a, b) => {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLng = ((b.lng - a.lng) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
};

const dirUrl = (b) =>
  `https://www.google.com/maps/dir/?api=1&destination=${b.lat},${b.lng}`;

function FlyController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (target) map.flyTo([target.lat, target.lng], 15, { duration: 1.1 });
  }, [target, map]);
  return null;
}

export const Locator = () => {
  const [branches, setBranches] = useState([]);
  const [activeSlug, setActiveSlug] = useState(null);
  const [target, setTarget] = useState(null);
  const [nearest, setNearest] = useState(null);
  const [locating, setLocating] = useState(false);
  const [geoError, setGeoError] = useState("");

  useEffect(() => {
    fetchBranches().then(setBranches).catch(() => setBranches([]));
  }, []);

  const center = useMemo(() => ({ lat: 18.62, lng: 73.83 }), []);

  const select = (b) => {
    setActiveSlug(b.slug);
    setTarget({ lat: b.lat, lng: b.lng, t: Date.now() });
  };

  const findNearest = () => {
    setGeoError("");
    if (!navigator.geolocation) {
      setGeoError("Location not supported on this device.");
      return;
    }
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const me = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        let best = null;
        let bestD = Infinity;
        branches.forEach((b) => {
          const d = distKm(me, b);
          if (d < bestD) {
            bestD = d;
            best = b;
          }
        });
        setNearest({ branch: best, km: bestD });
        select(best);
        setLocating(false);
      },
      () => {
        setGeoError("Couldn't get your location. Please allow location access.");
        setLocating(false);
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  return (
    <section
      id="visit"
      data-testid="locator-section"
      className="relative bg-[#1F1A18] px-6 lg:px-10 py-24 lg:py-32"
    >
      <div className="max-w-[1400px] mx-auto">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-10">
          <div>
            <span className="text-xs tracking-[0.3em] uppercase font-bold text-[#F59E0B]">
              Store Locator
            </span>
            <h2 className="mt-5 font-display uppercase text-[#F8F4EC] text-5xl sm:text-6xl lg:text-7xl leading-[0.9]">
              Reach <span className="text-[#F59E0B]">us</span>
            </h2>
          </div>
          <button
            onClick={findNearest}
            data-testid="locator-find-nearest"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-[#F59E0B] text-[#141110] font-semibold hover:shadow-[0_0_28px_rgba(245, 158, 11,0.45)] hover:scale-[1.02] active:scale-95 transition-all duration-300"
          >
            <LocateFixed size={18} className={locating ? "animate-pulse" : ""} />
            {locating ? "Locating…" : "Find nearest branch"}
          </button>
        </div>

        {nearest && (
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            data-testid="locator-nearest"
            className="mb-6 rounded-xl border border-[#F59E0B]/40 bg-[#F59E0B]/10 px-5 py-4 flex flex-wrap items-center justify-between gap-3"
          >
            <p className="text-[#F8F4EC]">
              Nearest branch:{" "}
              <span className="font-semibold text-[#F59E0B]">{nearest.branch.name}</span>{" "}
              <span className="text-[#BFAFA6]">· {nearest.km.toFixed(1)} km away</span>
            </p>
            <a
              href={dirUrl(nearest.branch)}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-[#141110] bg-[#F59E0B] px-4 py-2 rounded-full text-sm font-semibold"
            >
              <Navigation size={14} /> Directions
            </a>
          </motion.div>
        )}
        {geoError && (
          <p data-testid="locator-geo-error" className="mb-6 text-[#e5817a] text-sm">
            {geoError}
          </p>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* branch list */}
          <div className="lg:col-span-4 order-2 lg:order-1">
            <div className="rounded-2xl border border-white/10 bg-white/[0.03] max-h-[560px] overflow-y-auto divide-y divide-white/10">
              {branches.map((b) => (
                <button
                  key={b.slug}
                  onClick={() => select(b)}
                  data-testid={`locator-branch-${b.slug}`}
                  className={`w-full text-left p-4 transition-colors duration-300 ${
                    activeSlug === b.slug ? "bg-[#F59E0B]/10" : "hover:bg-white/[0.04]"
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <MapPin
                      size={16}
                      className={`shrink-0 mt-0.5 ${
                        activeSlug === b.slug ? "text-[#E04F33]" : "text-[#F59E0B]"
                      }`}
                    />
                    <div>
                      <div className="font-semibold text-[#F8F4EC]">{b.name}</div>
                      <div className="text-xs text-[#BFAFA6] mt-0.5">{b.address}</div>
                      <a
                        href={dirUrl(b)}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        className="inline-flex items-center gap-1 text-xs text-[#F59E0B] mt-2 hover:underline"
                      >
                        Get directions <ArrowUpRight size={12} />
                      </a>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* map */}
          <div className="lg:col-span-8 order-1 lg:order-2">
            <div
              className="rounded-2xl overflow-hidden border border-white/10 h-[420px] lg:h-[560px]"
              data-testid="locator-map"
            >
              <MapContainer
                center={[center.lat, center.lng]}
                zoom={11}
                scrollWheelZoom={false}
                style={{ height: "100%", width: "100%", background: "#141110" }}
              >
                <TileLayer
                  attribution='&copy; OpenStreetMap &copy; CARTO'
                  url="https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"
                />
                {branches.map((b) => (
                  <Marker
                    key={b.slug}
                    position={[b.lat, b.lng]}
                    icon={activeSlug === b.slug ? iconActive : icon}
                    eventHandlers={{ click: () => setActiveSlug(b.slug) }}
                  >
                    <Popup>
                      <div style={{ minWidth: 160 }}>
                        <strong>{b.name}</strong>
                        <div style={{ fontSize: 12, margin: "4px 0" }}>{b.address}</div>
                        <a href={dirUrl(b)} target="_blank" rel="noopener noreferrer">
                          Get directions →
                        </a>
                      </div>
                    </Popup>
                  </Marker>
                ))}
                <FlyController target={target} />
              </MapContainer>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
